import { Eyes, Folder, Printer, QrCode } from "@phosphor-icons/react";
import Link from "astro-typesafe-routes/link/react";
import { $path } from "astro-typesafe-routes/path";
import React, { Suspense, useMemo, useState } from "react";
import { match } from "ts-pattern";

import { useLocale } from "~/hooks/locale";
import type { FragmentType } from "~/utils/fragment";
import { slugify } from "~/utils/slug";

import { AuthForm } from "../AuthForm";
import { CopyLink } from "../CopyLink";
import { CycleLoadingText } from "../CycleLoadingText";
import { DialogHeader } from "../DialogHeader";
import { FullSizeQRCode } from "../FullSizeQRCode";
import { MenuAction, MenuList } from "../Menu";
import { Modal, type ModalType } from "../Modal";
import { AssignPack } from "./AssignPack";
import { DeletePack } from "./DeletePack";
import { DuplicatePack } from "./DuplicatePack";
import type { PackActions_PackFragment } from "./PackActions_PackFragment";
import { PreviewPack } from "./PreviewPack";
import { UserPackActions } from "./UserPackActions";

const PrintPack = React.lazy(() => import("./PrintPack"));

export type PackAction =
  | "preview"
  | "print"
  | "qr"
  | "duplicate"
  | "delete"
  | "assign"
  | "login";

export type OmitAction = PackAction | "edit" | "link";

export const PackActions: React.FC<{
  omit?: OmitAction[];
  pack: FragmentType<"Pack", { select: typeof PackActions_PackFragment }>;
}> = ({ pack, omit = [] }) => {
  const { t } = useLocale();
  const [action, setAction] = useState<PackAction | null>(null);

  const path = useMemo(
    () => $path({ to: "/packs/[slug]", params: { slug: slugify(pack) } }),
    [pack],
  );

  return (
    <>
      <MenuList>
        <ul className="menu">
          {!omit.includes("preview") && (
            <li>
              <MenuAction
                icon={Eyes}
                onClick={() => {
                  setAction("preview");
                }}
              >
                {t("preview")}
              </MenuAction>
            </li>
          )}
          {!omit.includes("link") && (
            <li>
              <CopyLink path={path} />
            </li>
          )}
          {!omit.includes("qr") && (
            <li>
              <MenuAction
                icon={QrCode}
                onClick={() => {
                  setAction("qr");
                }}
              >
                {t("show_qr_code")}
              </MenuAction>
            </li>
          )}
          <li>
            <MenuAction
              icon={Printer}
              onClick={() => {
                setAction("print");
              }}
            >
              {t("print")}
            </MenuAction>
          </li>
          {pack.collection && (
            <li>
              <Link
                to="/collections/[slug]"
                params={{ slug: slugify(pack.collection) }}
              >
                <Folder weight="bold" className="size-4" />
                {t("view_collection")}
              </Link>
            </li>
          )}
          <UserPackActions omit={omit} setAction={setAction} pack={pack} />
        </ul>
      </MenuList>
      <Modal
        size={action === "assign" ? "lg" : "md"}
        type={match(action)
          .returnType<ModalType>()
          .with("qr", () => "qr")
          .with("print", () => "drawer")
          .otherwise(() => "dialog")}
        isOpen={action !== null}
        onClose={() => {
          setAction(null);
        }}
      >
        {action &&
          match(action)
            .with("preview", () => <PreviewPack pack={pack} />)
            .with("print", () => (
              <>
                <DialogHeader>Print pack</DialogHeader>
                <div className="grid min-h-0 grow divide-x sm:grid-cols-[var(--sidebar)_1fr] sm:[--sidebar:288px] md:[--sidebar:320px] lg:[--sidebar:384px]">
                  <Suspense
                    fallback={
                      <>
                        <div />
                        <div className="flex bg-base-200 max-sm:hidden">
                          <div className="m-auto">
                            <CycleLoadingText
                              delay={3000}
                              messages={[
                                "Loading fonts...",
                                "Preparing the PDF...",
                                "Formatting pages...",
                                "Assembling letters...",
                              ]}
                            />
                          </div>
                        </div>
                      </>
                    }
                  >
                    <PrintPack packId={pack.id} />
                  </Suspense>
                </div>
              </>
            ))
            .with("qr", () => (
              <FullSizeQRCode value={window.location.origin + path} />
            ))
            .with("duplicate", () => <DuplicatePack pack={pack} />)
            .with("delete", () => <DeletePack pack={pack} />)
            .with("assign", () => <AssignPack pack={pack} />)
            .with("login", () => <AuthForm />)
            .exhaustive()}
      </Modal>
    </>
  );
};
